import { useEffect, useState } from "react";
import useSearch from "../../context/Search/SearchContext";
import { useNavigate } from "../../hooks";
import Routes from "../../routes";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Black, White } from "../../color_utils";
import { trakrefFontFamily, trakrefFontFamilySemiBold, isMobile } from "../../utils";
import SearchResultsTab from "./SearchResultsTab";
import { Clipboard, RightArrow } from "../../Icons/Icons";
import { NoData } from "../Common/DataGrid/utils";
import NoDataTableIcon from '../../Icons/empty_table_placeholder.svg';
const SearchResults = () => {
    const initialState = {
        workOrders: { data: [], isSearchById: false },
        assets: { data: [], isSearchById: false },
        locations: { data: [], isSearchById: false },
        serviceEvents: { data: [], isSearchById: false },
    };
    const navigate = useNavigate({
        pageName: "SearchResults",
    });
    const [value, setValue] = useState(0);
    const { state } = useSearch();
    const [debounceTimer, setDebounceTimer] = useState(null);
    const [properties, setProperties] = useState({
        ...initialState,
        allowNew: false,
        isLoading: true,
        multiple: false,
        keyword: null,
        options: [],
    });

    useEffect(() => {
        if (state.lastQuery === null || state.lastQuery === "") {
            return navigate.goBack();
        }
        if (state.lastQuery && state.lastQuery.length < 3) return;
        if (debounceTimer) clearTimeout(debounceTimer);
        setDebounceTimer(
            setTimeout(() => {
                search(state.lastQuery);
            }, 1000)
        );

        return () => {
            if (debounceTimer) clearTimeout(debounceTimer);
        };
    }, [state.lastQuery]);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const search = (query) => {
        setProperties((prevProperties) => ({
            ...prevProperties,
            ...initialState,
            keyword: query,
            isLoading: true,
        }));

        const device = isMobile ? 'mobile' : 'desktop';
        fetch(Routes.Dashboard.Search(device), {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify(query),
        })
            .then((response) => response.json())
            .then((json) => {
                // console.log(json);
                const newState = {
                    isLoading: false,
                    keyword: query,
                    options: json,
                    workOrders: processSearchData(
                        json.filter((obj) => obj.link.includes("WorkOrder")),
                        "woNumber",
                        query
                    ),
                    assets: processSearchData(
                        json.filter((obj) => obj.link.includes("Inventory")),
                        "woNumber",
                        query
                    ),
                    locations: processSearchData(
                        json.filter((obj) => obj.link.includes("Location")),
                        "locationName",
                        query
                    ),
                    serviceEvents: processSearchData(
                        json.filter((obj) => obj.link.includes("ServiceEvent")),
                        "text",
                        query
                    ),
                };
                return setProperties(newState);
            })
            .catch((err) => console.log("Error fetching options", err));
    };

    const highlightKeyword = (item, searchTerm) => {
        if (!searchTerm) return item;
        const regex = new RegExp(`(${searchTerm})`, "gi");
        const parts = item.split(regex);

        const text = parts.map((part, index) => {
            if (part.toLowerCase() === searchTerm.toLowerCase()) {
                return (
                    <span key={index}>
                        {part}
                    </span>
                );
            }

            return <span key={index}>{part}</span>;
        });

        return text;
    };

    const getCount = (data, key) => {
        return data.reduce(
            (accumulator, currentValue) => accumulator + currentValue[key].count,
            0
        );
    };

    const mapSearchData = ({
        items,
        titleKey,
        subTitleKey,
        isIdAtSubtitle = false,
        searchTerm = "",
    }) => {
        const data = items.map((item) => {
            const title = highlightKeyword(item[titleKey], searchTerm);
            const subTitle = highlightKeyword(subTitleKey(item), searchTerm);
            const link = item.link;
            return { title, subTitle, link };
        });
        const titleCount = getCount(data, "title");
        const subTitleCount = getCount(data, "subTitle");
        const isSearchById = subTitleCount > titleCount;
        const isOrCondition = subTitleCount > 0 && titleCount > 0;

        return {
            data,
            isSearchById: isIdAtSubtitle ? !isSearchById : isSearchById,
            isOrCondition,
        };
    };

    const processSearchData = (data, key, searchTerm) => {
        const mappedSearchData = mapSearchData({
            items: data,
            titleKey: key,
            subTitleKey: (item) =>
                // \u2022 = bullet character (•)
                `${item.locationName} ${item.code ? `\u2022 ${item.code}` : ""} `
                + `\u2022 ${item.type ? item.type : item.status} \u2022 ${item.woStatus ? item.woStatus : item.id}`,
            searchTerm,
        });
        return mappedSearchData;
    };

    if (properties.isLoading) {
        return <Box sx={{ display: "flex", flexDirection: "row", gap: 2, pt: { xs: "8px", md: 0 } }}>
            <Typography
                sx={{
                    "color": Black,
                    "font-size": ".95rem",
                    "font-family": trakrefFontFamilySemiBold,
                    "margin-bottom": "0 !important",
                    "padding-bottom": "0 !important",
                    "margin-left": "10px",
                }}
            >
                Searching for {state.query ? state.query : "..."}
            </Typography>
            <CircularProgress size="1rem" />
        </Box>;
    }


    const tabData = [
        { key: "workOrders", label: "Work Orders" },
        { key: "locations", label: "Locations" },
        { key: "assets", label: "Assets" },
        { key: "serviceEvents", label: "Service Events" },
    ];

    const renderResults = (items) => {

        if (items.length === 0) {
            return <NoData
                noTableDataIcon={NoDataTableIcon}
                noResultText={"No search matches here"}
                noResultText2={"Update your search or explore other categories."}

            />
        }
        return items.map((item) => (
            <Box
                key={item.link}
                className="ResultItemContainer flex f-row f-ac"
                onClick={() => { navigate.push(item.link); }}
                sx={{
                    backgroundColor: { xs: 'transparent', md: White },
                    py: "16px !important",
                    m: "0 !important",
                    borderTop: "1px solid #E0E0E0",
                }}
            >
                <Box className="WOItemText" sx={{ marginLeft: "10px !important" }}>
                    <Typography className="SearchHeaderText" sx={{ color: `${Black} !important` }}>
                        {item.title}
                    </Typography>
                    <p className="Subtext">
                        {item.subTitle}
                    </p>
                </Box>
            </Box>
        ));
    }
    return (
        <Box sx={{ p: { xs: 1, lg: 0 } }}>
            <Typography
                variant="h6"
                sx={{
                    marginY: "14px",
                    color: Black,
                    fontFamily: trakrefFontFamily,
                }}
            >
                Search Results
            </Typography>
            <Typography
                variant="subtitle1"
                sx={{
                    fontFamily: trakrefFontFamily,
                }}
            >
                Results for "{state.lastQuery}"
            </Typography>
            <SearchResultsTab
                value={value}
                handleChange={handleChange}
                notification={{
                    workOrder: properties.workOrders.data.length,
                    location: properties.locations.data.length,
                    inventory: properties.assets.data.length,
                    serviceEvent: properties.serviceEvents.data.length
                }}
            />
            <Box sx={{ marginTop: "16px" }}>
                {renderResults(properties[tabData[value].key].data)}
            </Box>
        </Box>
    );
};

export default SearchResults;
